import { Link } from "react-router-dom";
import {
  Github,
  Linkedin,
  Mail,
  Cpu,
  Code,
  ShieldCheck,
  Globe,
} from "lucide-react";
import logo from '../../Images/LogoOM.PNG'


export default function ToolsFooter() {
  const currentYear = new Date().getFullYear();

  const toolGroups = [
    {
      title: "Engineering & NDT",
      icon: <Cpu size={14} className="text-cyan-600" />,
      links: [
        { name: "Thickness Calculator", path: "/thicknesscalculator" },
        { name: "Weld Size Checker", path: "/weldchecker" },
        { name: "Material Database", path: "/materials" },
      ],
    },
    {
      title: "Developer Tools",
      icon: <Code size={14} className="text-blue-600" />,
      links: [
        { name: "Password Generator", path: "/advancedpassword" },
        { name: "QR Code Generator", path: "/qrgenerator" },
        { name: "JSON Formatter", path: "/json-tool" },
      ],
    },
    {
      title: "Resources",
      icon: <Globe size={14} className="text-slate-600" />,
      links: [
        { name: "All Tools", path: "/tools" },
        { name: "Documentation", path: "/docs" },
        { name: "API Access", path: "/api" },
      ],
    },
  ];

  return (
<footer className="bg-white border-t border-slate-200 text-slate-600 pt-12 pb-6 px-4">
  <div className="max-w-7xl mx-auto">

        {/* MAIN GRID */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">

          {/* BRAND */}
          <div className="space-y-4 text-center sm:text-left">
        <Link to="/" className="flex items-center gap-3 group mb-6">
                     <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center transition-transform group-hover:rotate-12">
                       <img src={logo} alt="Logo" className="w-8 h-8 object-contain" />
                     </div>
                     <span className="text-2xl font-black text-black tracking-tighter uppercase">
                       Omkar More
                     </span>
                   </Link>

            <p className="text-sm text-slate-500 leading-relaxed">
              Professional online tools for engineers, developers and technicians.
              Free NDT calculators, welding inspection utilities and secure developer tools.
            </p>

            {/* SOCIAL */}
            <div className="flex justify-center sm:justify-start gap-4 pt-2">
              <a
                href="https://github.com/omkarmore"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub Profile"
                className="hover:text-slate-900 transition"
              >
                <Github size={18} />
              </a>

              <a
                href="https://linkedin.com/in/omkarmore"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn Profile"
                className="hover:text-blue-700 transition"
              >
                <Linkedin size={18} />
              </a>

              <a
                href="mailto:moreomkar972@gmail.com"
                aria-label="Send Email"
                className="hover:text-slate-900 transition"
              >
                <Mail size={18} />
              </a>
            </div>
          </div>

          {/* TOOL GROUPS */}
          {toolGroups.map((group) => (
            <nav key={group.title} aria-label={group.title} className="text-center sm:text-left">
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-5 flex items-center justify-center sm:justify-start gap-2">
                {group.icon}
                {group.title}
              </h3>

              <ul className="space-y-3">
                {group.links.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-sm font-medium hover:text-cyan-600 transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        {/* TRUST / FEATURES */}
        <div className="border-t border-slate-100 pt-6 pb-4">
          <div className="flex flex-wrap justify-center gap-4 text-xs font-semibold text-slate-400 uppercase tracking-wide">
            <span className="flex items-center gap-1">
              <ShieldCheck size={12} className="text-green-500" />
              Engineering Accuracy
            </span>

            <span className="flex items-center gap-1">
              <ShieldCheck size={12} className="text-green-500" />
              Secure Tools
            </span>

            <span className="flex items-center gap-1">
              <ShieldCheck size={12} className="text-green-500" />
              Free Utilities
            </span>
          </div>
        </div>

        {/* COPYRIGHT */}
        <div className="mt-6 pt-6 border-t border-slate-100 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-slate-400">

          <div className="flex flex-wrap justify-center gap-4">
            <span>© {currentYear} Developed by Omkar More.</span>

            <Link to="/privacy" className="hover:text-slate-600">
              Privacy Policy
            </Link>

            <Link to="/terms" className="hover:text-slate-600">
              Terms of Service
            </Link>
          </div>

          <div className="flex items-center gap-1">
            Made with <span className="text-red-500">❤</span> for Engineers
          </div>
        </div>
      </div>

      {/* SEO Hidden Keywords */}
      <div className="sr-only">
        Free online NDT tools, ultrasonic thickness calculator, welding inspection calculator,
        developer tools, password generator, QR code generator, engineering calculators,
        tools built by Omkar More.
      </div>

      {/* STRUCTURED DATA */}
      <script type="application/ld+json">
        {`
        {
          "@context":"https://schema.org",
          "@type":"Organization",
          "name":"OM.Engine Tools",
          "url":"https://omkarmore.com/tools",
          "founder":{
            "@type":"Person",
            "name":"Omkar More"
          }
        }
        `}
      </script>
    </footer>
  );
}